import React, { useMemo } from "react";

import { Col } from "antd/es/grid";
import Row from "antd/es/row";
import { ColumnType } from "antd/es/table";
import { IntlShape } from "react-intl";

import DuplicateStatus from "@mapmycustomers/shared/enum/DuplicateStatus";
import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { EntityTypeSupportingDataMerging } from "@mapmycustomers/shared/types/entity";
import { DuplicatePair } from "@mapmycustomers/shared/types/entity/Duplicate";
import { Popconfirm } from "@mapmycustomers/ui";

import DuplicateCell from "@app/component/DuplicateCell";
import RoundButton from "@app/component/RoundButton";
import ButtonType from "@app/enum/ButtonType";
import { changeDuplicatePairDismission } from "@app/scene/settings/component/MergeDuplicates/store/actions";
import { dataMergingAnalytics } from "@app/scene/settings/settingsAnaytics";
import layout from "@app/styles/layout";
import { getEntityTypeDisplayName } from "@app/util/ui";

import styles from "./DuplicateColumns.module.scss";

const useDuplicateColumns = (
  intl: IntlShape,
  entityType: EntityTypeSupportingDataMerging,
  changeDuplicatePairDismissionAction: typeof changeDuplicatePairDismission.request,
  onPreview: (index: number) => void
): ColumnType<DuplicatePair>[] =>
  useMemo(
    () => [
      {
        className: styles.cell,
        dataIndex: "primary",
        render: (_, row) => <DuplicateCell duplicate={row.primary} entityType={entityType} />,
        title: (
          <div className={styles.columnHeader}>
            {intl.formatMessage(
              {
                id: "settings.mergeDuplicates.table.column.primary",
                defaultMessage: "{entityType} A",
                description: "Title of primary column",
              },
              {
                entityType: getEntityTypeDisplayName(intl, entityType, {
                  lowercase: false,
                  plural: false,
                }),
              }
            )}
          </div>
        ),
      },
      {
        className: styles.cell,
        dataIndex: "secondary",
        render: (_, row) => <DuplicateCell duplicate={row.secondary} entityType={entityType} />,
        title: (
          <div className={styles.columnHeader}>
            {intl.formatMessage(
              {
                id: "settings.mergeDuplicates.table.column.secondary",
                defaultMessage: "{entityType} B",
                description: "Title of secondary column",
              },
              {
                entityType: getEntityTypeDisplayName(intl, entityType, {
                  lowercase: false,
                  plural: false,
                }),
              }
            )}
          </div>
        ),
      },
      {
        className: styles.cell,
        dataIndex: "id",
        render: (_, row, index) => (
          <Row gutter={[layout.spacerM, layout.spacerS]}>
            <Col>
              {row.status === DuplicateStatus.ERRORED ? (
                <Popconfirm
                  cancelText={intl.formatMessage({
                    id: "settings.mergeDuplicates.table.reMerge.confirmation.cancel",
                    defaultMessage: "No",
                    description:
                      "Settings Merge duplicates - preview/merge table - Merge error confirmation cancel",
                  })}
                  description={intl.formatMessage({
                    id: "settings.mergeDuplicates.table.reMerge.confirmation.title",
                    defaultMessage:
                      "There was a problem merging these records. Would you like to try again?",
                    description:
                      "Settings Merge duplicates - preview/merge table - Merge error confirmation title",
                  })}
                  okText={intl.formatMessage({
                    id: "settings.mergeDuplicates.table.reMerge.confirmation.confirm",
                    defaultMessage: "Try again",
                    description:
                      "Settings Merge duplicates - preview/merge table - Merge error confirmation ok",
                  })}
                  onConfirm={() => onPreview(index)}
                >
                  <RoundButton type={ButtonType.DANGER}>
                    {intl.formatMessage({
                      id: "settings.mergeDuplicates.table.mergeError",
                      defaultMessage: "Merge Error",
                      description: "Title of Merge error action",
                    })}
                  </RoundButton>
                </Popconfirm>
              ) : (
                <RoundButton
                  disabled={row.status === DuplicateStatus.ONGOING}
                  onClick={() => {
                    dataMergingAnalytics?.clicked([
                      getEntityTypeDisplayName(intl, entityType as EntityType, {
                        lowercase: false,
                        plural: true,
                      }),
                      "Review & Merge",
                    ]);
                    onPreview(index);
                  }}
                >
                  {row.status === DuplicateStatus.ONGOING
                    ? intl.formatMessage({
                        id: "settings.mergeDuplicates.table.mergeOngoing",
                        defaultMessage: "Merging in progress...",
                        description: "Title of Merge ongoing text",
                      })
                    : intl.formatMessage({
                        id: "settings.mergeDuplicates.table.reviewMerge",
                        defaultMessage: "Review & Merge",
                        description: "Title of Review merge action",
                      })}
                </RoundButton>
              )}
            </Col>
            <Col>
              <RoundButton
                onClick={() => {
                  dataMergingAnalytics?.clicked([
                    getEntityTypeDisplayName(intl, entityType as EntityType, {
                      lowercase: false,
                      plural: true,
                    }),
                    row.status === DuplicateStatus.DISMISSED ? "Undo dismission" : "Dismiss",
                  ]);
                  changeDuplicatePairDismissionAction({
                    dismissed: !(row.status === DuplicateStatus.DISMISSED),
                    pair: row,
                  });
                }}
                type={ButtonType.SECONDARY}
              >
                {row.status === DuplicateStatus.DISMISSED
                  ? intl.formatMessage({
                      id: "settings.mergeDuplicates.table.undoDismiss",
                      defaultMessage: "Undo dismission",
                      description: "Title of Undo dismission action",
                    })
                  : intl.formatMessage({
                      id: "settings.mergeDuplicates.table.dismiss",
                      defaultMessage: "Dismiss",
                      description: "Title of dismiss action",
                    })}
              </RoundButton>
            </Col>
          </Row>
        ),
        title: (
          <div className={styles.columnHeader}>
            {intl.formatMessage({
              id: "settings.mergeDuplicates.table.column.c",
              defaultMessage: "Actions",
              description: "Title of actions column",
            })}
          </div>
        ),
      },
    ],
    [intl, entityType, changeDuplicatePairDismissionAction, onPreview]
  );

export default useDuplicateColumns;
